import { Auth } from 'aws-amplify';
import { userAppData } from './MainApp.js';
import appConfig from '../config';

const SCHOOL_LOGO_BUCKET_BASE_URL = 'https://schoolengine-schoollogos.s3.eu-west-2.amazonaws.com';
export const PROFILE_IMAGE_UPDATED_EVENT = 'school-engine:profile-image-updated';

/**
 * Builds the public school logo URL from the user's school code.
 * Returns an empty string when no usable school code is available.
 * @param {string} schoolCode
 * @returns {string}
 */
export function getSchoolLogoUrl(schoolCode) {
    const cleanSchoolCode = String(schoolCode || '').trim();
    if (!cleanSchoolCode) {
        return '';
    }
    return `${SCHOOL_LOGO_BUCKET_BASE_URL}/${cleanSchoolCode}.png`;
}

/**
 * Returns the user's profile image when set, otherwise the school's logo URL.
 * @param {string} profileImageUrl
 * @param {string} schoolCode
 * @returns {string}
 */
export function getProfileImageUrlWithSchoolFallback(profileImageUrl, schoolCode) {
    const cleanProfileImageUrl = String(profileImageUrl || '').trim();
    if (cleanProfileImageUrl) {
        return cleanProfileImageUrl;
    }
    return getSchoolLogoUrl(schoolCode);
}



////// ARRAY SORTING FUNCTIONS

export function sortArrayByNumericalProperty(array, key, order = 'ascending') {
    /*
        This function takes three parameters:

        array: The array of objects you wish to sort.
        key: The property name (as a string) of the objects in the array that you want to sort by.
        order: A string indicating the sort order. It can be either 'ascending' or 'descending'. If not specified, the default order is 'ascending'.
        In the sortArrayByProperty function, the sort method is called on the array. Inside the sort method, the values of the specified property (key) are first converted to integers (using parseInt) because your epoch values are represented as strings in the given array. Then, based on the order specified, the function compares these values to sort the array accordingly.

        In the example usage, the dataCallsState array is sorted by the submissionEpoch property in descending order, so the object with the highest submissionEpoch value will be first, and the one with the lowest value will be last.
        */
    return array.sort((a, b) => {
        let valueA = parseInt(a[key], 10); // assuming the property is a string that should be compared as an integer
        let valueB = parseInt(b[key], 10); // assuming the property is a string that should be compared as an integer

        if (order.toLowerCase() === 'ascending') {
            return valueA - valueB;
        } else { // default to descending if order is not explicitly 'ascending'
            return valueB - valueA;
        }
    });
}

// same as above for for alphabetical
export function sortArrayByProperty(array, key, order = 'ascending') {
    return array.sort((a, b) => {
        let valueA = a[key];
        let valueB = b[key];

        // Check if the values are strings that contain only digits (likely numerical)
        if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
            valueA = parseInt(valueA, 10);
            valueB = parseInt(valueB, 10);
        }

        if (order.toLowerCase() === 'ascending') {
            if (valueA < valueB) {
                return -1;
            }
            if (valueA > valueB) {
                return 1;
            }
            return 0;
        } else { // default to descending if order is not explicitly 'ascending'
            if (valueA > valueB) {
                return -1;
            }
            if (valueA < valueB) {
                return 1;
            }
            return 0;
        }
    });
}

//////  DATE AND TIME FUNCTIONS
export function thisWeekEpochRange() {
    const now = Date.now();
    const { startOfWeek, setHours, setMinutes, setSeconds, setMilliseconds, format, parseISO } = require('date-fns');
    // Get the current date and time
    const currentDate = new Date();
    // Calculate the date for the previous Monday at 1 am
    const previousMonday = setMilliseconds(setSeconds(setMinutes(setHours(startOfWeek(currentDate), 1), 0), 0), 0);
    // Format the date to a string in ISO 8601 format and parse it back to get the epoch time (in seconds)
    let lowerThresholdEpoch = Math.floor(parseISO(format(previousMonday, "yyyy-MM-dd'T'HH:mm:ss.SSSX")) / 1000);
    let upperThresholdEpoch = now;

    const epochRanges = {
        'upper': upperThresholdEpoch,
        'lower': lowerThresholdEpoch
    };

    return (epochRanges);

};

export function lastWeekEpochRange() {
    const { startOfWeek, setHours, setMinutes, setSeconds, setMilliseconds, format, parseISO } = require('date-fns');
    // Get the current date and time
    const currentDate = new Date();
    // Calculate the date for the previous Monday at 1 am
    const previousMonday = setMilliseconds(setSeconds(setMinutes(setHours(startOfWeek(currentDate), 1), 0), 0), 0);
    // Format the date to a string in ISO 8601 format and parse it back to get the epoch time (in seconds)
    let upperThresholdEpoch = Math.floor(parseISO(format(previousMonday, "yyyy-MM-dd'T'HH:mm:ss.SSSX")) / 1000);
    let lowerThresholdEpoch = upperThresholdEpoch - 604800;

    const epochRanges = {
        'upper': upperThresholdEpoch,
        'lower': lowerThresholdEpoch
    };

    return (epochRanges);
};


export function epochToReadable(gmtSeconds) {
    let d = new Date(0); // The 0 there is the key, which sets the date to the epoch
    d.setUTCSeconds(gmtSeconds);
    let formattedTime = d.toLocaleString("en-GB", {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
    return (formattedTime);
};

export function epochToReadableTime(gmtSeconds) {
    let d = new Date(0); // The 0 there is the key, which sets the date to the epoch
    d.setUTCSeconds(gmtSeconds);
    let formattedTime = d.toLocaleString("en-GB", {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false // This ensures you get a 24-hour format
    });
    return (formattedTime);
};




function handleErrors(response, apiErrorStateSet = () => { }) {
    if (!response.ok) {
        try {
            apiErrorStateSet(true);
        } catch (_) {
            // no-op if a non-function was provided
        }
    }
    return response;
};

function buildApiErrorFromPayload(payload, fallbackMessage = 'API returned an error.') {
    if (payload instanceof Error) {
        return payload;
    }

    if (typeof payload === 'string' && payload.trim()) {
        return new Error(payload.trim());
    }

    if (payload && typeof payload === 'object') {
        const candidateMessage = payload.message || payload.errorMessage || payload.error || payload.detail;
        if (typeof candidateMessage === 'string' && candidateMessage.trim()) {
            return new Error(candidateMessage.trim());
        }
    }

    return new Error(fallbackMessage);
}

function payloadRepresentsApiError(payload) {
    if (payload == null) {
        return false;
    }

    if (typeof payload === 'object') {
        const numericStatusCode = Number(payload.statusCode);
        if (!Number.isNaN(numericStatusCode) && numericStatusCode >= 400) {
            return true;
        }

        if (payload.data === 'error') {
            return true;
        }

        if (
            typeof payload.message === 'string'
            && payload.message.trim().toLowerCase() === 'missing authentication token'
        ) {
            return true;
        }

        if (payload.error === true) {
            return true;
        }
    }

    return false;
}


export function SEC_ApiCall(primaryUrl, params = {}, apiErrorStateSet = () => { }) {
    return new Promise((resolve, reject) => {
        Auth.currentAuthenticatedUser({ bypassCache: true })
            .then(user => {
                const newToken = user["signInUserSession"]["idToken"]["jwtToken"];
                userAppData['idToken'] = newToken;

                // Build URL with parameters
                const entries = Object.entries(params);

                let url = 'https://api.schoolengine.co.uk/' + appConfig['APP_API_STAGE'] + '/' + primaryUrl;

                if (entries.length > 0) {
                    const queryString = entries
                        .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value ?? '')}`)
                        .join('&');
                    url += `?${queryString}`;
                }

                fetch(url, {
                    method: 'GET',
                    headers: { 'Authorization': newToken }
                })
                    .then(response => handleErrors(response, apiErrorStateSet))
                    .then(async response => {
                        let data = null;
                        try {
                            data = await response.json();
                        } catch (parseError) {
                            if (!response.ok) {
                                throw buildApiErrorFromPayload(parseError, `API request failed with status ${response.status}`);
                            }
                            throw parseError;
                        }

                        if (!response.ok || payloadRepresentsApiError(data)) {
                            throw buildApiErrorFromPayload(
                                data,
                                `API request failed with status ${response.status}`
                            );
                        }

                        return data;
                    })
                    .then(data => {
                        resolve(data);
                    })
                    .catch(error => {
                        console.log('api fetch error', error);
                        apiErrorStateSet(true);
                        reject(error); // ✅ now valid
                    });
            })
            .catch(err => {
                console.log("Error getting authenticated user", err);
                apiErrorStateSet(true);
                reject(err); // ✅ now valid
            });
    });
}




async function generateNewToken() {
    const response = await Auth.currentAuthenticatedUser({ bypassCache: true });
    let newToken = response["signInUserSession"]["idToken"]["jwtToken"];
    userAppData['idToken'] = newToken;
    return newToken;
};


export async function SEC_FileApiPost(primaryUrl, body, apiErrorStateSet) {
    const response = await fetch(primaryUrl, { method: 'POST', body });

    if (!response.ok) {
        apiErrorStateSet(true);
        throw new Error(`File upload failed with status: ${response.status}`);
    }

    return response;
}


export async function SEC_DataApiPost(primaryUrl, body, params = {}, apiErrorStateSet = () => { }) {
    try {
        const token = await generateNewToken();
        // add params to url
        const entries = Object.entries(params || {});

        let url = 'https://api.schoolengine.co.uk/' + appConfig['APP_API_STAGE'] + '/' + primaryUrl;

        if (entries.length > 0) {
            let count = 0;
            for (const [name, value] of entries) {
                if (count === 0) {
                    url = url + '?' + name + '=' + value;
                    count = count + 1;
                } else {
                    url = url + '&' + name + '=' + value;
                    count = count + 1;
                }
            };
        };
        const submit = await fetch(url, { method: 'POST', body: body, headers: { 'Authorization': token } });
        const response = handleErrors(submit, apiErrorStateSet);
        const data = await response.json();

        if (!response.ok || payloadRepresentsApiError(data)) {
            apiErrorStateSet(true);
            throw buildApiErrorFromPayload(
                data,
                `API request failed with status ${response.status}`
            );
        }

        return data;
    } catch (e) {
        try {
            apiErrorStateSet('error');
        } catch (_) {
            // swallow if callback is missing/invalid
        }
        console.log(e);
        throw e;
    }
};


export function fileUploadFormDataGenerator(uploadUrl, item) {
    const formData = new FormData();
    const fields = uploadUrl.fields;

    // Append AWS-generated fields from Lambda (excluding metadata keys you'll explicitly add later)
    const metadataKeys = ["x-amz-meta-userid", "x-amz-meta-originalname", "x-amz-meta-submitted", "Content-Type"];

    Object.entries(fields).forEach(([key, value]) => {
        if (!metadataKeys.includes(key)) {
            formData.append(key, value);
        }
    });

    // Now explicitly add metadata fields exactly once with correct values
    formData.append("x-amz-meta-userid", item.metadata.userid);
    formData.append("x-amz-meta-originalname", item.metadata.originalname);
    formData.append("x-amz-meta-submitted", item.metadata.submitted);
    formData.append("Content-Type", item.file.type || "application/octet-stream");

    // Add file itself
    formData.append("file", item.file);

    return formData;
}






export function accessBoolean(val) {
    if (val > 0) {
        return true;
    } else {
        return false;
    }
}

export async function downloadFileFromS3({
    fileId,
    oldName,
    newName = null,
    folder,
    itemId = null,
    dispatch = null,
    forceDownload = false
}) {
    try {
        const displayFileName = oldName || newName || 'download';
        const extension = displayFileName.split('.').pop().toLowerCase();
        const previewable = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
        const shouldOpenInBrowser = previewable.includes(extension);
        const previewWindow = shouldOpenInBrowser && !forceDownload ? window.open('about:blank', '_blank') : null;

        if (previewWindow) {
            previewWindow.opener = null;
        }
        const urlParams = {
            locationId: itemId,
            requestType: folder,
            fileOldName: oldName   // ✅ add this
        };
        if (fileId) {
            urlParams.fileId = fileId;
        }
        if (!fileId && newName) {
            urlParams.objectName = newName;
        }
        if (forceDownload) {
            urlParams.downloadMode = 'attachment';
        }

        const response = await SEC_ApiCall('general/SignedURLs', urlParams);

        if (shouldOpenInBrowser && !forceDownload) {
            if (previewWindow) {
                previewWindow.location.href = response;
            } else {
                window.open(response, '_blank', 'noopener,noreferrer');
            }
        } else if (forceDownload) {
            const fileResponse = await fetch(response);
            if (!fileResponse.ok) {
                throw new Error(`File download failed with status: ${fileResponse.status}`);
            }
            const fileBlob = await fileResponse.blob();
            const objectUrl = window.URL.createObjectURL(fileBlob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = displayFileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);
        } else {
            const a = document.createElement('a');
            a.href = response;
            a.download = displayFileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
        }

    } catch (error) {
        console.error('Download failed:', error);
        if (dispatch) {
            dispatch({
                type: 'SET_MODAL',
                payload: {
                    active: true,
                    apiState: 'error',
                    message: 'File could not be downloaded',
                },
            });
        }
    }
}
